import { cn, tv } from "tailwind-variants";
import { ResponsiveImage } from "#/_packages/umbrella/components/ui/responsive-image";
import type {
	AnimationScrollType,
	AspectRatio,
	BoxVariant,
	ImageMedia,
	VerticalSelfAlignment,
} from "#/_packages/umbrella/types";

export type ImageBlockRounded =
	| "default"
	| "none"
	| "xs"
	| "sm"
	| "md"
	| "lg"
	| "xl"
	| "full";

export type ImageBlockLink = {
	url?: string;
	href?: string;
	target?: "_self" | "_blank" | "_parent" | "_top";
	title?: string;
	noFollow?: boolean;
	trackingEvent?: string;
};

export type ImageBlockProps = ImageMedia & {
	id?: string | null;
	aspectRatio?: AspectRatio;
	rounded?: ImageBlockRounded;
	boxVariant?: BoxVariant;
	alignmentSelfVertical?: VerticalSelfAlignment;
	animationScrollType?: AnimationScrollType;
	hoverItemClasses?: string | null;
	hoverMediaClasses?: string | null;
	link?: ImageBlockLink;
	className?: string;
};

const imageBlock = tv({
	slots: {
		figure: "block__image figure relative w-full",
		image: "figure-img w-full object-cover",
		imageWrapper: "overflow-hidden",
		caption: "figure-caption",
	},
	variants: {
		aspectRatio: {
			none: { image: "" },
			"1:1": { image: "aspect-square" },
			"4:3": { image: "aspect-[4/3]" },
			"3:2": { image: "aspect-[3/2]" },
			"16:9": { image: "aspect-video" },
			"21:9": { image: "aspect-[21/9]" },
		},
		rounded: {
			default: { imageWrapper: "" },
			none: { imageWrapper: "rounded-0" },
			xs: { imageWrapper: "rounded-sm" },
			sm: { imageWrapper: "rounded" },
			md: { imageWrapper: "rounded-md" },
			lg: { imageWrapper: "rounded-lg" },
			xl: { imageWrapper: "rounded-xl" },
			full: { imageWrapper: "rounded-full" },
		},
		boxVariant: {
			none: { figure: "" },
			background: {
				figure:
					"card-background rounded-[var(--umb-border-radius-xxl)] border border-border-base bg-surface-muted p-3",
			},
			outline: {
				figure:
					"card-outline rounded-[var(--umb-border-radius-xxl)] border border-border-base p-3",
			},
			shadow: {
				figure:
					"card-shadow rounded-[var(--umb-border-radius-xxl)] border border-border-base bg-surface-base p-3 shadow-md",
			},
		},
		alignmentSelfVertical: {
			stretch: { figure: "" },
			start: { figure: "self-start" },
			center: { figure: "self-center" },
			end: { figure: "self-end" },
		},
		animationScrollType: {
			none: { figure: "" },
			"anim-view-fadeIn-moveUp": { figure: "anim-view-fadeIn-moveUp" },
			"anim-view-fadeIn-moveDown": { figure: "anim-view-fadeIn-moveDown" },
			"anim-view-fadeIn-moveLeft": { figure: "anim-view-fadeIn-moveLeft" },
		},
	},
	defaultVariants: {
		alignmentSelfVertical: "stretch",
		animationScrollType: "none",
	},
});

function resolveHref(link: ImageBlockLink) {
	return link.href || link.url || "#";
}

export function ImageBlock({
	id,
	src,
	alt,
	caption,
	aspectRatio = "none",
	rounded = "default",
	boxVariant = "none",
	alignmentSelfVertical = "stretch",
	animationScrollType = "none",
	hoverItemClasses,
	hoverMediaClasses,
	link,
	className,
	...imageAttributes
}: ImageBlockProps) {
	const {
		figure,
		image,
		imageWrapper,
		caption: captionClass,
	} = imageBlock({
		aspectRatio,
		rounded,
		boxVariant,
		alignmentSelfVertical,
		animationScrollType,
	});

	const imageMarkup = (
		<>
			<div className={imageWrapper()}>
				<ResponsiveImage
					className={image()}
					image={{ src, alt, ...imageAttributes }}
				/>
			</div>
			{caption ? (
				<figcaption className={captionClass()}>{caption}</figcaption>
			) : null}
		</>
	);

	return (
		<figure
			id={id ?? undefined}
			className={cn(figure(), hoverMediaClasses, className)}
		>
			{link ? (
				<a
					href={resolveHref(link)}
					target={link.target}
					data-tracking-event={link.trackingEvent}
					title={link.title}
					rel={
						[
							link.target === "_blank" ? "noopener noreferrer" : "",
							link.noFollow ? "nofollow" : "",
						]
							.filter(Boolean)
							.join(" ") || undefined
					}
					className={cn(
						hoverItemClasses,
						"block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring",
					)}
				>
					{imageMarkup}
				</a>
			) : (
				imageMarkup
			)}
		</figure>
	);
}
